import { X } from 'lucide-react';
import { useState } from 'react';
import { formatEther } from 'viem';
import { useEVMTransaction } from '../../../../hooks/useEvmTransaction';
import { classifyTransaction } from '../../../../lib/evm/transaction';
import { createDeclineTransactionMessage } from '../../../../lib/transaction-messages';
import { shortenString } from '../../../../lib/utils';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../ui/accordion';
import { CardFooter, CardHeader } from '../../../ui/card';
import { CopyStandard } from '../../CopyStandard';
import DefaultTxApproveButton from '../../default-components/DefaultTxApproveButton';
import DefaultTxContainer from '../../default-components/DefaultTxContainer';
import DefaultTxDeclineButton from '../../default-components/DefaultTxDeclineButton';
import LoadingMessage from '../../loading/LoadingMessage';
import { TransactionDetail } from '../TransactionDetail';
import { TransactionResult } from '../TransactionResult';
import { EvmTransactionCardProps } from './types';

export const EvmTransactionCard = ({
  evmData,
  uiData,
  messageBackgroundColor,
  borderColor,
  textColor,
  customTxContainer: TxContainer = DefaultTxContainer,
  customApproveTxButton: ApproveButton = DefaultTxApproveButton,
  customDeclineTxButton: DeclineButton = DefaultTxDeclineButton,
  append,
  messageId,
  classification: providedClassification,
}: EvmTransactionCardProps) => {
  const {
    isLoading,
    errorMsg,
    transaction,
    handleSmartAction,
    network,
    evmAddress,
  } = useEVMTransaction({ evmData, append, messageId });

  const [isDeclined, setIsDeclined] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);

  // Use provided classification or compute it
  const classification = providedClassification ?? classifyTransaction(evmData);
  const { method, params } = evmData;

  const declineTransaction = async () => {
    setIsDeclining(true);
    try {
      await append(createDeclineTransactionMessage(messageId));
      setIsDeclined(true);
    } catch (error) {
      console.error('Error declining transaction:', error);
    } finally {
      setIsDeclining(false);
    }
  };

  if (isDeclined) {
    return (
      <TxContainer
        style={{
          backgroundColor: messageBackgroundColor,
          borderColor,
          textColor,
        }}
      >
        <div className="bitte-p-6 bitte-text-center">
          <X className="bitte-mx-auto bitte-mb-2 bitte-h-8 bitte-w-8 bitte-text-red-500" />
          <p>Transaction declined</p>
        </div>
      </TxContainer>
    );
  }

  if (transaction && transaction.txHash) {
    return (
      <TransactionResult
        result={{
          evm: { txHash: transaction.txHash, chainId: evmData.chainId },
        }}
        accountId={evmAddress || ''}
        textColor={textColor}
        success={transaction.status === 'success'}
      />
    );
  }

  const loadingIndicator = (
    <div>
      <LoadingMessage color={textColor} />
      <p className="bitte-text-center bitte-text-sm">
        {isDeclining
          ? 'Declining transaction...'
          : `Transaction for ${
              typeof params[0] === 'object' && params[0]?.to
                ? shortenString(params[0].to, 8)
                : 'contract'
            } in progress`}
      </p>
    </div>
  );

  if (isLoading || isDeclining) {
    return (
      <TxContainer
        style={{
          backgroundColor: messageBackgroundColor,
          borderColor,
          textColor,
        }}
      >
        <div className="bitte-p-6">{loadingIndicator}</div>
      </TxContainer>
    );
  }

  return (
    <TxContainer
      style={{
        backgroundColor: messageBackgroundColor,
        borderColor,
        textColor,
      }}
    >
      <CardHeader
        className="bitte-p-4 bitte-text-center bitte-md:p-6"
        style={{ borderColor: borderColor }}
      >
        <div className="bitte-flex bitte-items-center bitte-justify-center bitte-gap-2">
          <p className="bitte-text-xl bitte-font-semibold">
            {classification.title}
          </p>
        </div>
        <p className="bitte-mt-2 bitte-text-xs bitte-opacity-80">
          {classification.description}
        </p>
      </CardHeader>

      <div className="bitte-p-6">
        <div className="bitte-flex bitte-flex-col bitte-gap-6 bitte-text-sm">
          <TransactionDetail
            label="Network"
            value={
              <div className="bitte-flex bitte-items-center bitte-gap-2">
                {network?.icon && (
                  <img
                    src={network.icon}
                    alt={network.name}
                    className="bitte-h-4 bitte-w-4"
                  />
                )}
                <span>{network?.name || ''}</span>
              </div>
            }
          />

          {method === 'eth_sendTransaction' && (
            <div className="">
              {params.map((transaction, index) => (
                <div
                  key={`tx-${typeof transaction === 'object' ? transaction.to : ''}-${index}`}
                  className="bitte-space-y-4"
                >
                  <div className="bitte-space-y-4 ">
                    <div className="bitte-flex bitte-items-center bitte-justify-between">
                      <span className="bitte-font-medium">To:</span>
                      <div className="bitte-flex bitte-items-center bitte-gap-2">
                        <CopyStandard
                          text={
                            typeof transaction === 'object'
                              ? transaction.to
                              : ''
                          }
                        />
                      </div>
                    </div>

                    {typeof transaction === 'object' &&
                      BigInt(transaction.value) > 0n && (
                        <div className="bitte-flex bitte-items-center bitte-justify-between">
                          <span className="bitte-font-medium bitte-flex bitte-items-center bitte-gap-1">
                            Value:
                          </span>
                          <span className="bitte-font-mono bitte-font-semibold bitte-text-green-600 bitte-flex bitte-items-center bitte-gap-1">
                            {formatEther(
                              BigInt(
                                typeof transaction === 'object'
                                  ? transaction.value
                                  : '0x0',
                              ),
                            )}{' '}
                            {uiData?.token?.symbol || ''}
                            {uiData?.token?.icon && (
                              <img
                                src={uiData.token.icon}
                                alt={uiData.token?.symbol}
                                className="bitte-w-4 bitte-h-4 bitte-rounded-full bitte-ml-1"
                              />
                            )}
                          </span>
                        </div>
                      )}

                    {typeof transaction === 'object' &&
                      transaction.data &&
                      transaction.data !== '0x' && (
                        <div className="bitte-rounded-lg bitte-bg-gray-900 bitte-p-3 bitte-border bitte-border-gray-700">
                          <Accordion type="single" collapsible>
                            <AccordionItem
                              value={`data-${index}`}
                              className="bitte-border-none"
                            >
                              <AccordionTrigger className="bitte-text-sm bitte-text-white bitte-hover:text-gray-300 bitte-justify-between bitte-items-center">
                                <span className="bitte-flex-shrink-0">
                                  Transaction Data
                                </span>
                                <div onClick={(e) => e.stopPropagation()}>
                                  <CopyStandard
                                    text={
                                      typeof transaction === 'object'
                                        ? transaction.data
                                        : ''
                                    }
                                    textSize="xs"
                                    charSize={25}
                                  />
                                </div>
                              </AccordionTrigger>
                              <AccordionContent>
                                <div className="bitte-max-h-[250px] bitte-overflow-y-auto">
                                  <pre className="bitte-text-xs bitte-whitespace-pre-wrap bitte-break-all bitte-text-white">
                                    {typeof transaction === 'object'
                                      ? transaction.data
                                      : ''}
                                  </pre>
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        </div>
                      )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {errorMsg && (
        <div className="bitte-p-4">
          <div className="bitte-rounded-lg bitte-bg-red-500/10 bitte-border bitte-border-gray-700 bitte-p-3">
            <p className="bitte-text-red-500 bitte-text-sm">{errorMsg}</p>
          </div>
        </div>
      )}

      <CardFooter className="bitte-flex bitte-gap-3 bitte-p-4">
        <DeclineButton
          onClick={declineTransaction}
          isLoading={isDeclining}
          disabled={isLoading}
          label="Decline"
        />
        <ApproveButton
          onClick={handleSmartAction}
          isLoading={isLoading}
          disabled={isDeclining}
          label="Send Transaction"
        />
      </CardFooter>
    </TxContainer>
  );
};
